import React, { useState } from 'react'
import { Button } from '@mytours/components'

import ConfirmationModal from '../../common/components/confirmation-modal'
import ProgressRing from '../../offline/components/ProgressRing'
import { formatBytes, percentComplete } from '../../offline/functions'
import { useCollectionDownload } from '../../offline/useDownload'
import strings from '../../../strings'
import { downloadGateLabel, downloadLabel, downloadRowState, WebDownloadPolicy } from '../functions'

import './styles/download.less'

interface Props {
	collectionId: number
	/** `off` never reaches here; the footer renders its usual buttons instead. */
	policy: Exclude<WebDownloadPolicy, 'off'>
	/** The published size, which is native's figure and overstates. A label, not a budget. */
	fileSize?: number
	/** The language the visitor is reading in, which a saved tour has to match to count as saved. */
	language: string
	/** What to call this download where a visitor manages their storage. */
	title?: string
	startButton: React.ReactNode
}

const TourDownload: React.FC<Props> = ({ collectionId, policy, fileSize, language, title, startButton }) => {
	const [confirmingRemove, setConfirmingRemove] = useState(false)
	const {
		status,
		progress,
		language: savedLanguage,
		supported,
		start,
		cancel,
		retryNow,
		removeNow,
	} = useCollectionDownload(collectionId, title)

	// A browser with no service worker or Cache API — an in-app browser, a private window — has nowhere to
	// download into. A forced tour there falls back to a plain start rather than a gate nothing can open.
	if (!supported) return <>{startButton}</>

	const state = downloadRowState(status, savedLanguage, language)
	const { downloading, failed, otherLanguage, saved } = state
	const percent = percentComplete(progress)

	const removeConfirmation = (
		<ConfirmationModal
			isOpen={confirmingRemove}
			question={strings.remove_download}
			onDidDismiss={() => setConfirmingRemove(false)}
			onAccept={() => {
				setConfirmingRemove(false)
				removeNow()
			}}
		/>
	)

	if (policy === 'optional') {
		return (
			<>
				{startButton}
				<Button
					variant="clear"
					className="btn download-btn"
					aria-label={downloadLabel(state, fileSize)}
					onClick={() => {
						if (downloading) cancel()
						else if (saved) setConfirmingRemove(true)
						else if (failed) retryNow()
						else start()
					}}
				>
					{downloading ? (
						<ProgressRing percent={percent} />
					) : (
						<i className={`fal fa-${saved ? 'circle-check' : failed ? 'circle-exclamation' : 'circle-down'}`} />
					)}
					{!downloading && !saved && !failed && <span className="download-size">{formatBytes(fileSize)}</span>}
				</Button>
				{removeConfirmation}
			</>
		)
	}

	// `forced`: the download is the button, and it is a hard gate until it has run.
	if (saved) {
		return (
			<>
				{startButton}
				<Button
					variant="clear"
					className="btn download-btn"
					aria-label={strings.remove_download}
					onClick={() => setConfirmingRemove(true)}
				>
					<i className="fal fa-circle-check" />
				</Button>
				{removeConfirmation}
			</>
		)
	}

	if (downloading) {
		return (
			<Button variant="clear" className="btn download-gate" aria-label={strings.downloading_tour} onClick={cancel}>
				<ProgressRing percent={percent} />
				<span>{`${strings.downloading_tour} ${percent}%`}</span>
				<i className="fal fa-xmark" aria-hidden="true" />
			</Button>
		)
	}

	// Only a real failure opens a way past the gate — and it says so, rather than leaving a visitor
	// standing in the museum with a dead screen.
	if (failed) {
		return (
			<>
				<Button variant="clear" className="btn download-gate" aria-label={strings.retry} onClick={retryNow}>
					{strings.retry}
				</Button>
				<div className="download-start-anyway">{startButton}</div>
			</>
		)
	}

	return (
		<Button
			variant="clear"
			className="btn download-gate"
			aria-label={otherLanguage ? strings.download_new_language : downloadGateLabel(fileSize)}
			onClick={start}
		>
			{otherLanguage ? strings.download_new_language : downloadGateLabel(fileSize)}
		</Button>
	)
}

export default TourDownload
